import React, { useContext } from "react";
import {
  Box,
  Typography,
  Menu,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BuildIcon from "@mui/icons-material/Build";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { formatDistanceToNow } from "date-fns";
import { ThemeContext } from "../../utils/ThemeContext";

/* ── Relative-time helper ──────────────────────────────────────────────── */
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return "";
  }
};

/* ── Per-type metadata ─────────────────────────────────────────────────── */
const TYPE_META = {
  work_order: {
    label: (id) => `New Work Order: WO-${id}`,
    icon: <BuildIcon sx={{ fontSize: 18 }} />,
    iconBg: (dark) => (dark ? "#1a3a5c" : "#e3f0ff"),
    iconColor: (dark) => (dark ? "#7eb8f7" : "#1565c0"),
    path: (id) => `/home/workorders/${id}`,
  },
  violation: {
    label: (id) => `New Violation: VLT-${id}`,
    icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
    iconBg: (dark) => (dark ? "#3a2a10" : "#fff8e1"),
    iconColor: (dark) => (dark ? "#ffca61" : "#e65100"),
    path: (id) => `/home/violations/${id}`,
  },
};

const getFallbackMeta = (type, id) => ({
  label: () => `Notification${type ? ` (${type})` : ""} #${id ?? ""}`,
  icon: <NotificationsIcon sx={{ fontSize: 18 }} />,
  iconBg: (dark) => (dark ? "#1a2c46" : "#f0f4f8"),
  iconColor: (dark) => (dark ? "#8a9ab8" : "#546e7a"),
  path: () => "/home",
});

/* ── Component ─────────────────────────────────────────────────────────── */
const NotificationMenu = ({ anchorEl, notifications, onClose, onClear, navigate }) => {
  const { isDark } = useContext(ThemeContext);

  const paperBg   = isDark ? "#1a2c46" : "#ffffff";
  const borderClr = isDark ? "rgba(255,255,255,0.08)" : "#eef2f7";
  const hoverBg   = isDark ? "rgba(255,255,255,0.05)" : "#f5f8ff";
  const titleClr  = isDark ? "#dde5f0" : "#1a2332";
  const dateClr   = isDark ? "#6b7c96" : "#9aa5b4";
  const emptyClr  = isDark ? "#6b7c96" : "#9aa5b4";

  const handleClick = (notification) => {
    const meta = TYPE_META[notification.type] ?? getFallbackMeta(notification.type, notification.id);
    navigate(meta.path(notification.id));
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundColor: paperBg,
          boxShadow: isDark
            ? "0 18px 45px rgba(0,0,0,0.5)"
            : "0 18px 45px rgba(0,0,0,0.14)",
          border: `1px solid ${borderClr}`,
          mt: 1,
          width: 380,
          maxWidth: "calc(100vw - 32px)",
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 2,
          pb: 1.25,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: titleClr }}>
          Notifications
          {notifications.length > 0 && (
            <Typography
              component="span"
              variant="caption"
              sx={{
                ml: 1,
                px: 0.75,
                py: 0.2,
                borderRadius: "20px",
                backgroundColor: isDark ? "rgba(47,99,173,0.25)" : "rgba(47,99,173,0.1)",
                color: "#2f63ad",
                fontWeight: 700,
              }}
            >
              {notifications.length}
            </Typography>
          )}
        </Typography>

        {notifications.length > 0 && (
          <Tooltip title="Clear all">
            <IconButton
              size="small"
              onClick={() => { onClear?.(); onClose(); }}
              sx={{ color: isDark ? "#6b7c96" : "#9aa5b4", "&:hover": { color: "#d32f2f" } }}
            >
              <DeleteSweepIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: borderClr }} />

      {/* ── List ── */}
      <Box sx={{ maxHeight: 360, overflowY: "auto" }}>
        {notifications.length > 0 ? (
          [...notifications]
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map((notification, index) => {
              const meta =
                TYPE_META[notification.type] ??
                getFallbackMeta(notification.type, notification.id);

              return (
                <Box
                  key={notification.id ?? index}
                  onClick={() => handleClick(notification)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    borderBottom: `1px solid ${borderClr}`,
                    "&:last-child": { borderBottom: "none" },
                    "&:hover": { backgroundColor: hoverBg },
                    transition: "background-color 0.15s",
                  }}
                >
                  {/* Type icon */}
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "10px",
                      backgroundColor: meta.iconBg(isDark),
                      color: meta.iconColor(isDark),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {meta.icon}
                  </Box>

                  {/* Text */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? "#dde5f0" : "#223142",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {meta.label(notification.id)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: dateClr }}>
                      {timeAgo(notification.date)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
        ) : (
          <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
            <NotificationsIcon sx={{ fontSize: 36, color: isDark ? "#2a3c54" : "#e0e7f0", mb: 1 }} />
            <Typography variant="body2" sx={{ color: emptyClr, fontWeight: 500 }}>
              You're all caught up!
            </Typography>
            <Typography variant="caption" sx={{ color: isDark ? "#4a5c74" : "#c4cdd8" }}>
              No new notifications
            </Typography>
          </Box>
        )}
      </Box>
    </Menu>
  );
};

export default NotificationMenu;
